import { Grid, Box, Heading, SimpleGrid, Text, Flex, Icon } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { simulate_gen_range } from '../pkg/rng_v1_web'
import { useEffect, useState } from 'react'
import { sha256 } from '@noble/hashes/sha256'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { EmptyPlaceholder } from '../molecules/EmptyPlaceholder'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { FormProvider, useForm } from 'react-hook-form'
import { VerifyFormInput } from '../atoms/VerifyFormInput'
import TicketIcon from '@/public/assets/icons/ticket.svg'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Section } from '../utils'

const schema = yup
  .object({
    signature: yup
      .string()
      .required()
      .label('Signature')
      .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),
    tickets: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .label('Tickets')
      .min(1),
  })
  .required()

export const JackpotVerify = () => {
  const { t } = useTranslation()
  const form = useForm({
    resolver: yupResolver(schema),
  })

  const [state, setState] = useState<{ maxTickets: number; result: number } | undefined>()

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  const handleClick = async () => {
    await form.trigger()
    if (!form.formState.isValid) {
      setState(undefined)
      return
    }

    const signature = form.getValues('signature').replace('0x', '')
    const tickets = Number(form.getValues('tickets'))

    setState({
      maxTickets: tickets,
      result: parseInt(
        simulate_gen_range(
          Buffer.from(sha256(Buffer.from(signature, 'hex'))).toString('hex'),
          Buffer.from('jackpot').toString('hex'),
          1,
          '0',
          (tickets + 1).toString(),
          false
        )
      ),
    })
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', lg: '260px auto' }}>
      <Box p={5} rounded={'24px'} bg={'ninja.600'}>
        <FormProvider {...form}>
          <VerifyFormTitle mode="drand" />
          <VerifyFormTextarea
            label={t('verify.signature')}
            placeholder={t('verify.enter-signature')}
            name={'signature'}
          />
          <VerifyFormInput
            label={t('verify.tickets')}
            placeholder={t('verify.enter-tickets')}
            name={'tickets'}
            type={'number'}
          />
          <VerifyFormButton onClick={handleClick} />
        </FormProvider>
      </Box>
      <Box p={5} minH={{ base: '0', lg: '375px' }} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'} mb={{ base: 2, lg: 0 }}>
          {t('common.result')}
        </Heading>
        {!state ? (
          <EmptyPlaceholder game={Section.JACKPOT} />
        ) : (
          <JackpotVerifyResult result={state.result} maxTickets={state.maxTickets} />
        )}
      </Box>
    </Grid>
  )
}

const JackpotVerifyResult = ({ result, maxTickets }: { result: number; maxTickets: number }) => {
  return (
    <Flex alignItems={'center'} justifyContent={'center'} h={{ base: 'auto', lg: 'calc(100% - 24px)' }}>
      <SimpleGrid
        minH={'112px'}
        rounded={'24px'}
        boxShadow={'0px -4px 45px 0px #68F27F inset'}
        columns={{ base: 1, lg: 2 }}
        w={'max-content'}
        mx={'auto'}
      >
        <Flex
          gap={2}
          justifyContent={'center'}
          alignItems={'center'}
          p={6}
          pb={{ base: 2, lg: 6 }}
          borderRight={{ base: 'none', lg: '1px solid' }}
          borderRightColor={{ base: 'none', lg: '#8784B720' }}
        >
          <Icon w={16} h={16} color={'brand.green'} as={TicketIcon} />
          <Text fontSize={'xl'} fontWeight={700}>
            {result}
          </Text>
        </Flex>
        <Flex
          gap={1}
          fontSize={'sm'}
          alignItems={'center'}
          justifyContent={'center'}
          fontWeight={700}
          color={'ninja.200'}
          p={6}
          pt={{ base: 0, lg: 6 }}
        >
          <Flex gap={1} alignItems={'center'}>
            <Icon w={'auto'} h={5} as={TicketIcon} />
            <Text>1</Text>
          </Flex>
          <Text>-</Text>
          <Flex gap={1} alignItems={'center'}>
            <Icon w={'auto'} h={5} as={TicketIcon} />
            <Text>{maxTickets}</Text>
          </Flex>
        </Flex>
      </SimpleGrid>
    </Flex>
  )
}
